/**
 * URL state hook for workspace pages (catalog, stock, ...).
 *
 * Path: selection (article/category/group focus) — owned by React Router.
 * Query string: sort, per-column filter, tree/flat mode, revenue window,
 * search term, collapsed-row set. Sort/filter/mode/window/search/collapsed
 * changes REPLACE history; selection changes PUSH (so the browser back
 * button can step through the recently-viewed entities).
 *
 * Multiple workspaces can coexist on the same page via the optional
 * ``namespace`` parameter — every query-string key gets the namespace
 * prefix (e.g. ``s_sort``, ``s_mode``, ``s_filter[col]``). The catalog
 * passes no namespace so its URLs stay backward-compatible.
 */

import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { RevenueWindow } from "../../api/client";
import {
  DATA_COLUMN_ORDER,
  DEFAULT_VIEW,
  type CatalogPreset,
  type LowStockRule,
} from "../../utils/catalogView";

export type Mode = "tree" | "flat";

export type SortDir = "asc" | "desc";

export interface WorkspaceUrlState {
  /** Active mode (tree by default; auto-flips to flat when an article column is sorted). */
  mode: Mode;
  /** Currently-active sort column (e.g. ``margin``, ``revenue``, ``name``). */
  sort: string | null;
  sortDir: SortDir;
  /** Revenue-window selector value. */
  window: RevenueWindow;
  windowFrom: string | null;
  windowTo: string | null;
  /** Free-text search box content. */
  q: string;
  /** Per-column filters; the value semantics depend on the column type. */
  filters: Record<string, string>;
  /**
   * Collapsed rows in tree mode, e.g. ``["cat:3", "grp:7"]``. Stored as a
   * comma-separated string in the URL.
   */
  collapsed: Set<string>;
  /** Catalog column preset (Pricing default / Stock / Custom). */
  preset: CatalogPreset;
  /** Explicit visible data columns when ``preset === "custom"``; else null. */
  cols: string[] | null;
  /** Low-stock highlight: on/off, baseline rule, threshold percent. */
  hlOn: boolean;
  hlRule: LowStockRule;
  hlPct: number;
}

const DEFAULT_STATE: WorkspaceUrlState = {
  mode: "tree",
  sort: null,
  sortDir: "desc",
  window: "alltime",
  windowFrom: null,
  windowTo: null,
  q: "",
  filters: {},
  collapsed: new Set(),
  preset: DEFAULT_VIEW.preset,
  cols: DEFAULT_VIEW.cols,
  hlOn: DEFAULT_VIEW.hlOn,
  hlRule: DEFAULT_VIEW.hlRule,
  hlPct: DEFAULT_VIEW.hlPct,
};

function parseCols(raw: string | null): string[] | null {
  if (!raw) return null;
  const wanted = new Set(raw.split(",").filter(Boolean));
  const out = DATA_COLUMN_ORDER.filter((c) => wanted.has(c));
  return out.length > 0 ? out : null;
}

function parsePct(raw: string | null): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 && n < 100
    ? Math.round(n)
    : DEFAULT_VIEW.hlPct;
}

function parseCollapsed(raw: string | null): Set<string> {
  if (!raw) return new Set();
  return new Set(raw.split(",").filter(Boolean));
}

function parseFilters(
  params: URLSearchParams,
  prefix: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  // ``[prefix]filter[col]=value`` — preserve everything between the
  // brackets as the column name; this lets the column key contain dots,
  // underscores, and other URL-safe characters.
  const filterKey = `${prefix}filter`;
  const pattern = new RegExp(
    `^${filterKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\[(.+)\\]$`,
  );
  for (const [key, value] of params.entries()) {
    const match = key.match(pattern);
    if (match) out[match[1]] = value;
  }
  return out;
}

export interface UseWorkspaceUrlStateOptions {
  /**
   * Optional query-string key prefix so multiple workspaces can coexist
   * on the same page. E.g. ``namespace: "s"`` produces ``s_sort``,
   * ``s_mode``, ``s_filter[col]``. Empty / undefined keeps the keys
   * unprefixed (catalog default).
   */
  namespace?: string;
}

export function useWorkspaceUrlState(
  options: UseWorkspaceUrlStateOptions = {},
): {
  state: WorkspaceUrlState;
  /** PUSH a new history entry; used for selection changes. */
  push: (next: Partial<WorkspaceUrlState>, path?: string) => void;
  /** REPLACE the current history entry; used for sort/filter/mode tweaks. */
  replace: (next: Partial<WorkspaceUrlState>) => void;
  /** Build a query string (no leading ?) from the current state for use
   * in PUSH navigations that need to preserve filters. */
  queryString: string;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const prefix = options.namespace ? `${options.namespace}_` : "";
  const k = (name: string) => `${prefix}${name}`;

  const state: WorkspaceUrlState = {
    mode: (searchParams.get(k("mode")) as Mode) || DEFAULT_STATE.mode,
    sort: searchParams.get(k("sort")),
    sortDir: (searchParams.get(k("sort_dir")) as SortDir) || "desc",
    window:
      (searchParams.get(k("window")) as RevenueWindow) || DEFAULT_STATE.window,
    windowFrom: searchParams.get(k("from_")),
    windowTo: searchParams.get(k("to")),
    q: searchParams.get(k("q")) || "",
    filters: parseFilters(searchParams, prefix),
    collapsed: parseCollapsed(searchParams.get(k("collapsed"))),
    preset:
      (searchParams.get(k("view")) as CatalogPreset) || DEFAULT_STATE.preset,
    cols: parseCols(searchParams.get(k("cols"))),
    hlOn: searchParams.get(k("hl")) !== "off",
    hlRule:
      searchParams.get(k("hlrule")) === "since_delivery"
        ? "since_delivery"
        : "crate",
    hlPct: searchParams.has(k("hlpct"))
      ? parsePct(searchParams.get(k("hlpct")))
      : DEFAULT_STATE.hlPct,
  };

  const buildParams = useCallback(
    (next: Partial<WorkspaceUrlState>): URLSearchParams => {
      const merged: WorkspaceUrlState = { ...state, ...next };
      // No namespace → rebuild from scratch (preserves the original
      // catalog behaviour where unrelated query params get stripped).
      // With a namespace → start from the current params and only
      // rewrite OUR keys so other namespaces on the same URL survive.
      const params =
        prefix === ""
          ? new URLSearchParams()
          : new URLSearchParams(searchParams);
      if (prefix !== "") {
        const ownKeys: string[] = [];
        for (const key of params.keys()) {
          if (
            key === k("mode") ||
            key === k("sort") ||
            key === k("sort_dir") ||
            key === k("window") ||
            key === k("from_") ||
            key === k("to") ||
            key === k("q") ||
            key === k("collapsed") ||
            key === k("view") ||
            key === k("cols") ||
            key === k("hl") ||
            key === k("hlrule") ||
            key === k("hlpct") ||
            key.startsWith(k("filter["))
          ) {
            ownKeys.push(key);
          }
        }
        for (const key of ownKeys) params.delete(key);
      }

      if (merged.mode !== DEFAULT_STATE.mode) params.set(k("mode"), merged.mode);
      if (merged.sort) params.set(k("sort"), merged.sort);
      if (merged.sort && merged.sortDir !== "desc")
        params.set(k("sort_dir"), merged.sortDir);
      if (merged.window !== DEFAULT_STATE.window)
        params.set(k("window"), merged.window);
      if (merged.windowFrom) params.set(k("from_"), merged.windowFrom);
      if (merged.windowTo) params.set(k("to"), merged.windowTo);
      if (merged.q) params.set(k("q"), merged.q);
      if (merged.collapsed.size > 0)
        params.set(k("collapsed"), Array.from(merged.collapsed).join(","));
      for (const [col, value] of Object.entries(merged.filters)) {
        if (value !== "") params.set(k(`filter[${col}]`), value);
      }
      // Catalog view config — omit defaults to keep URLs clean.
      if (merged.preset !== DEFAULT_STATE.preset)
        params.set(k("view"), merged.preset);
      if (merged.preset === "custom" && merged.cols && merged.cols.length > 0)
        params.set(k("cols"), merged.cols.join(","));
      if (!merged.hlOn) params.set(k("hl"), "off");
      if (merged.hlRule !== DEFAULT_STATE.hlRule)
        params.set(k("hlrule"), merged.hlRule);
      if (merged.hlPct !== DEFAULT_STATE.hlPct)
        params.set(k("hlpct"), String(merged.hlPct));
      return params;
    },
    // ``state`` is recreated every render but its values are URL-derived
    // — referencing it inside the callback is intentional and stable.
    // ``k`` is a closure over ``prefix`` (already in deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, prefix],
  );

  const replace = useCallback(
    (next: Partial<WorkspaceUrlState>) => {
      setSearchParams(buildParams(next), { replace: true });
    },
    [buildParams, setSearchParams],
  );

  const push = useCallback(
    (next: Partial<WorkspaceUrlState>, path?: string) => {
      const params = buildParams(next);
      const qs = params.toString();
      const target = path ?? window.location.pathname;
      navigate(qs ? `${target}?${qs}` : target);
    },
    [buildParams, navigate],
  );

  return {
    state,
    push,
    replace,
    queryString: searchParams.toString(),
  };
}
